import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowRight, ChevronRight, ShieldCheck, GitBranch, CheckCircle2,
  Building2, HardHat, Wrench, FileText, Flame, Globe2, LayoutDashboard,
  MessageCircle, Play, Download, Sparkles, Search, Map, Compass, Cog,
  Users, TrendingUp, Briefcase, FileCheck, Layers, Workflow as WorkflowIcon,
  ClipboardList, Rocket, Target,
} from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import IntroVideoModal from '../components/IntroVideoModal';
import ScreenshotMarquee from '../components/ScreenshotMarquee';
import AskPeterModal from '../components/AskPeterModal';
import CaseStudyModal from '../components/CaseStudyModal';
import TryVideraModal from '../components/TryVideraModal';
import CountUp from '../components/CountUp';
import { introVideo } from '../videos';

const V2Home = () => {
  const [askPeterOpen, setAskPeterOpen] = useState(false);
  const [caseStudyOpen, setCaseStudyOpen] = useState(false);
  const [tryOpen, setTryOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('try')) {
      setTryOpen(true);
      params.delete('try');
      navigate({ search: params.toString() ? `?${params.toString()}` : '' }, { replace: true });
    }
  }, [location.search, navigate]);

  useSEO({
    title: 'Keystone Strategic Consultants | Consultancy + Videra Technology',
    description: 'Keystone combines decades of project, lifecycle, healthcare, construction and PFI experience with Videra, the technology built from that experience. Start with Videra PM or build a bespoke Videra environment around your organisation.',
    path: '/',
  });

  const marqueeShots = [
    { src: '/images/nhs/nhs-portfolio-tracker.jpg' },
    { src: '/images/healthcare/hc-dashboard.jpg' },
    { src: '/images/nhs/nhs-pmo-command-centre.jpg' },
    { src: '/images/healthcare/hc-portfolio-tracker.jpg' },
    { src: '/images/nhs/nhs-governance-reports.jpg' },
    { src: '/images/healthcare/hc-handback-readiness.jpg' },
    { src: '/images/healthcare/hc-project-stages.jpg' },
    { src: '/images/nhs/nhs-ipc-dashboard.jpg' },
    { src: '/images/healthcare/hc-workspace-studio.jpg' },
    { src: '/images/nhs/nhs-board-pack.jpg' },
  ];

  return (
  <div data-testid="v2-home">
    <AskPeterModal open={askPeterOpen} onClose={() => setAskPeterOpen(false)} />
    <CaseStudyModal open={caseStudyOpen} onClose={() => setCaseStudyOpen(false)} />
    <TryVideraModal open={tryOpen} onClose={() => setTryOpen(false)} />

    {/* ============= HERO ============= */}
    <section className="relative pt-14 pb-16 md:pt-20 md:pb-24 overflow-hidden" data-testid="v2-home-hero">
      <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-white to-white" />
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, #0f172a 1px, transparent 0)',
        backgroundSize: '24px 24px',
      }} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-14 items-center">
          <div>
            <Link
              to="/clients"
              data-testid="v2-hero-vinci-badge"
              className="inline-flex items-center gap-2.5 bg-white border border-slate-200 rounded-full pl-2 pr-4 py-1.5 mb-6 hover:border-slate-900 transition-colors group"
            >
              <span className="inline-flex items-center gap-1.5 bg-slate-900 text-white text-[10px] uppercase tracking-[0.18em] font-bold px-2.5 py-1 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live
              </span>
              <img src="/images/clients/vinci-facilities.png" alt="VINCI Facilities" className="h-4 w-auto" />
              <span className="text-xs font-medium text-slate-700 group-hover:text-slate-900">Tier-1 PFI Hard Services on Videra</span>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-900 group-hover:translate-x-0.5 transition-all" />
            </Link>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 leading-[1.05]" data-testid="v2-hero-title">
              We&rsquo;ve lived the problems we help organisations solve.
            </h1>
            <p className="mt-5 text-lg lg:text-xl text-slate-600 leading-relaxed max-w-2xl">
              Keystone Strategic Consultants combines decades of real-world project, lifecycle, healthcare, construction and PFI experience with Videra, the technology we built from that experience.
            </p>
            <p className="mt-4 text-base lg:text-lg text-slate-600 leading-relaxed max-w-2xl">
              From an immediately available project management platform to a completely bespoke operational environment, Videra can grow around the complexity of your organisation.
            </p>
            <p className="mt-5 text-lg font-medium text-slate-900">
              Start today. Or build something around your world.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
              <a href="https://pm.videra.app"
                 data-testid="v2-hero-try"
                 className="inline-flex items-center justify-center bg-slate-900 text-white px-7 py-4 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors group shadow-lg shadow-slate-900/10 whitespace-nowrap">
                Start with Videra PM
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </a>
              <Link to="/contact"
                    data-testid="v2-hero-talk"
                    className="inline-flex items-center justify-center bg-white border border-slate-300 text-slate-900 px-7 py-4 rounded-md text-sm font-medium hover:border-slate-900 transition-colors">
                Talk to Keystone
                <ChevronRight className="ml-1 w-4 h-4" />
              </Link>
            </div>
          </div>

          <div className="relative animate-[videra-hero-image-in_.8s_.15s_ease-out_both]">
            <div className="relative bg-slate-900 rounded-2xl p-2 shadow-[0_30px_80px_-25px_rgba(15,23,42,0.55)] ring-1 ring-slate-900/5">
              <video
                src={introVideo.videoUrl}
                poster="/images/healthcare/hc-portfolio-tracker-hero.jpg"
                autoPlay muted loop playsInline preload="auto"
                data-testid="v2-hero-video"
                className="w-full aspect-[16/10] object-cover rounded-lg"
              />
            </div>
            <div className="absolute -inset-6 -z-10 bg-blue-500/10 blur-3xl rounded-full" aria-hidden="true" />
          </div>
        </div>
      </div>
      <style>{`@keyframes videra-hero-image-in { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </section>

    {/* ============= VINCI CLIENT PROOF ============= */}
    <section className="py-16 md:py-20 bg-slate-950 text-white" data-testid="v2-home-client-proof">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-10 lg:gap-16 items-center">
          <div className="bg-white rounded-2xl p-8 md:p-10 flex items-center justify-center min-w-[280px]">
            <img src="/images/clients/vinci-facilities.png" alt="VINCI Facilities" className="h-20 md:h-24 w-auto" />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-emerald-400 font-bold mb-4 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live in production
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight leading-[1.05]">
              A Tier-1 FM contractor chose Videra to run live PFI Hard Services on NHS hospital estates.
            </h2>
            <p className="mt-5 text-lg text-slate-300 leading-relaxed max-w-3xl">
              VINCI Facilities uses Videra technology to connect lifecycle requirements, projects, approvals, delivery, evidence and progressive handback readiness across hospital estates in the North and South of England.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/clients" data-testid="v2-home-vinci-story"
                    className="inline-flex items-center gap-2 bg-white text-slate-900 px-6 py-3 rounded-md text-sm font-medium hover:bg-slate-100 transition-colors">
                Read the VINCI story <ArrowRight className="w-4 h-4" />
              </Link>
              <button type="button" onClick={() => setCaseStudyOpen(true)} data-testid="v2-home-case-study-cta"
                      className="inline-flex items-center gap-2 text-sm font-medium text-blue-300 hover:text-blue-200 transition-colors">
                <Download className="w-4 h-4" /> Get the case study
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ============= LIVE NUMBERS TICKER ============= */}
    <section className="py-14 md:py-16 bg-slate-900 text-white border-b border-slate-800" data-testid="v2-home-numbers">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-[11px] uppercase tracking-[0.22em] text-blue-400 font-semibold mb-8 text-center">Live on Videra today</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 text-center">
          {[
            { value: 721, prefix: '£', suffix: 'M', label: 'Portfolio value under management' },
            { value: 57,  label: 'Active schemes & projects' },
            { value: 861, label: 'Days lifecycle horizon tracked' },
            { value: 24,  suffix: '/7', label: 'Evidence and audit rails' },
          ].map((n, i) => (
            <div key={i}>
              <CountUp value={n.value} prefix={n.prefix || ''} suffix={n.suffix || ''}
                       testid={`v2-home-numbers-${i}`}
                       className="text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight text-white block" />
              <p className="mt-2 text-xs md:text-sm text-slate-400 leading-snug max-w-[180px] mx-auto">{n.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ============= THREE WAYS TO START ============= */}
    <section className="py-20 md:py-24 bg-white" data-testid="v2-home-two-ways">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-14">
          <p className="text-[11px] uppercase tracking-[0.22em] text-blue-600 font-semibold mb-3">Three ways to start</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 leading-[1.05]">
            One philosophy. Three ways to start.
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Every organisation is different. Some simply need a better way to manage projects now. Others need technology designed around complex operational processes, lifecycle obligations, governance and contractual requirements. And some need independent expertise first, before any technology decision is made. That&rsquo;s why Keystone offers three distinct routes.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Option 1 — Videra PM */}
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-0.5 hover:border-blue-500 transition-all" data-testid="v2-home-option-pm">
            <div className="relative aspect-[16/9] bg-slate-900 overflow-hidden">
              <img src="/images/healthcare/hc-portfolio-tracker.jpg" alt="Videra PM" loading="lazy" className="w-full h-full object-cover object-top" />
              <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.18em] font-bold px-2.5 py-1 rounded bg-blue-600 text-white shadow-md">
                Option 1 · Start now
              </span>
            </div>
            <div className="p-6 md:p-7">
              <h3 className="text-2xl md:text-3xl font-medium tracking-tight text-slate-900">Videra PM</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">
                A ready-to-use project management platform you can personalise yourself. Pick an industry template, configure it around your team and start managing projects, no lengthy implementation required.
              </p>
              <ul className="mt-6 space-y-2.5 text-sm text-slate-700">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" /> Monthly subscription</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" /> Industry templates</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" /> Personalise it yourself</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" /> Rapid setup, no bespoke development</li>
              </ul>
              <p className="mt-5 text-sm font-medium text-slate-900">
                Start small. Prove the value. Grow when you&rsquo;re ready.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/videra/pm" data-testid="v2-home-option-pm-explore"
                      className="inline-flex items-center gap-2 bg-slate-900 text-white px-5 py-3 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors group">
                  Explore Videra PM
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </Link>
                <a href="https://pm.videra.app" data-testid="v2-home-option-pm-start"
                   className="inline-flex items-center gap-2 border border-slate-300 text-slate-900 px-5 py-3 rounded-md text-sm font-medium hover:border-slate-900 transition-colors">
                  Start Your Platform
                </a>
              </div>
            </div>
          </div>

          {/* Option 2 — Bespoke Videra */}
          <div className="bg-slate-950 text-white border border-slate-800 rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all" data-testid="v2-home-option-bespoke">
            <div className="relative aspect-[16/9] bg-slate-900 overflow-hidden">
              <img src="/images/nhs/nhs-pmo-command-centre.jpg" alt="Bespoke Videra" loading="lazy" className="w-full h-full object-cover object-top" />
              <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.18em] font-bold px-2.5 py-1 rounded bg-emerald-500 text-white shadow-md">
                Option 2 · Built around you
              </span>
            </div>
            <div className="p-6 md:p-7">
              <h3 className="text-2xl md:text-3xl font-medium tracking-tight">Bespoke Videra</h3>
              <p className="mt-3 text-slate-300 leading-relaxed">
                When your organisation needs more than project management. Keystone maps your processes, governance, assets and lifecycle requirements, then configures a Videra environment around them.
              </p>
              <ul className="mt-6 space-y-2.5 text-sm text-slate-300">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" /> Configured around your operating model</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" /> Connects projects, lifecycle, evidence</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" /> Governance and reporting built in</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" /> 14-day fully unlocked trial</li>
              </ul>
              <p className="mt-5 text-sm font-medium text-white">
                More than a platform. Part of the operating model.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/solutions" data-testid="v2-home-option-bespoke-explore"
                      className="inline-flex items-center gap-2 bg-white text-slate-900 px-5 py-3 rounded-md text-sm font-medium hover:bg-slate-100 transition-colors group">
                  Explore Bespoke Videra
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </Link>
                <Link to="/contact" data-testid="v2-home-option-bespoke-talk"
                      className="inline-flex items-center gap-2 border border-white/20 text-white px-5 py-3 rounded-md text-sm font-medium hover:border-white/50 transition-colors">
                  Talk to Keystone
                </Link>
              </div>
            </div>
          </div>

          {/* Option 3 — Keystone Consultancy */}
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 text-white border border-blue-500 rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all" data-testid="v2-home-option-consultancy">
            <div className="relative aspect-[16/9] bg-slate-900 overflow-hidden">
              <img src="/images/team/peter-fulford.jpg" alt="Keystone Consultancy" loading="lazy" className="w-full h-full object-cover object-center opacity-70" />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 via-blue-800/40 to-transparent" />
              <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.18em] font-bold px-2.5 py-1 rounded bg-white text-blue-700 shadow-md">
                Option 3 · Bring the problem first
              </span>
              <div className="absolute bottom-3 left-3 right-3">
                <p className="text-[11px] uppercase tracking-[0.22em] text-blue-100 font-semibold">Consultancy-led</p>
              </div>
            </div>
            <div className="p-6 md:p-7">
              <h3 className="text-2xl md:text-3xl font-medium tracking-tight text-white">Keystone Consultancy</h3>
              <p className="mt-3 text-blue-50 leading-relaxed">
                Sometimes the problem is bigger than software. Keystone brings decades of project, lifecycle, healthcare, construction and PFI experience to diagnose what&rsquo;s actually happening, before any technology decision is made.
              </p>
              <ul className="mt-6 space-y-2.5 text-sm text-blue-50">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-white mt-0.5 flex-shrink-0" /> Independent expertise first</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-white mt-0.5 flex-shrink-0" /> Diagnose, map and design</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-white mt-0.5 flex-shrink-0" /> No predetermined solution</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-white mt-0.5 flex-shrink-0" /> Software only if it fits</li>
              </ul>
              <p className="mt-5 text-sm font-medium text-white">
                Bring us the problem. We&rsquo;ll help decide where to start.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/contact" data-testid="v2-home-option-consultancy-talk"
                      className="inline-flex items-center gap-2 bg-white text-blue-700 px-5 py-3 rounded-md text-sm font-medium hover:bg-blue-50 transition-colors group">
                  Talk to Keystone
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </Link>
                <button type="button" onClick={() => setAskPeterOpen(true)} data-testid="v2-home-option-consultancy-ask"
                        className="inline-flex items-center gap-2 border border-white/40 text-white px-5 py-3 rounded-md text-sm font-medium hover:border-white transition-colors">
                  <MessageCircle className="w-4 h-4" /> Ask Peter
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ============= THE GROWTH JOURNEY ============= */}
    <section className="py-20 md:py-24 bg-slate-50 border-y border-slate-200" data-testid="v2-home-journey">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <p className="text-[11px] uppercase tracking-[0.22em] text-blue-600 font-semibold mb-3">The growth journey</p>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 leading-tight">
            From quick fix to connected operating environment.
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            You don&rsquo;t have to make a major technology decision on day one. Start with Videra PM. Use it. Personalise it. Improve project visibility. Prove the concept inside your organisation. And if your requirements grow, Keystone can work with you to take the next step.
          </p>
        </div>

        <div className="relative">
          <div className="absolute top-8 left-0 right-0 h-[2px] bg-slate-200 hidden md:block" aria-hidden="true" />
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-4 relative">
            {[
              { icon: Rocket, name: 'Videra PM', body: 'Ready-to-use platform' },
              { icon: Target, name: 'Prove', body: 'Use it in your organisation' },
              { icon: MessageCircle, name: 'Consult', body: 'Understand what else needs connecting' },
              { icon: Map, name: 'Design', body: 'Map your operating model' },
              { icon: Building2, name: 'Bespoke Videra', body: 'Build around your organisation' },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.name} className="text-center" data-testid={`v2-home-journey-step-${i}`}>
                  <div className={`w-16 h-16 mx-auto rounded-full bg-white ring-2 ${i === 0 || i === 4 ? 'ring-blue-500' : 'ring-slate-200'} flex items-center justify-center relative z-10 shadow-md`}>
                    <Icon className={`w-6 h-6 ${i === 0 || i === 4 ? 'text-blue-600' : 'text-slate-600'}`} strokeWidth={1.8} />
                  </div>
                  <p className="mt-4 text-sm md:text-base font-semibold text-slate-900">{s.name}</p>
                  <p className="mt-1 text-xs md:text-sm text-slate-600 leading-relaxed">{s.body}</p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-12 text-center">
          <p className="text-xl md:text-2xl font-medium tracking-tight text-slate-900 mb-6">
            Start where you are. Grow when you need to.
          </p>
          <a href="https://pm.videra.app" data-testid="v2-home-journey-cta"
             className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3.5 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors group">
            Start with Videra PM
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </a>
        </div>
      </div>
    </section>

    {/* ============= CONSULTANCY ============= */}
    <section className="py-20 md:py-24 bg-white" data-testid="v2-home-consultancy">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-16 items-start">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-blue-600 font-semibold mb-3">Consultancy</p>
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 leading-tight">
              For complex organisations, we start differently.
            </h2>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              Sometimes the problem is bigger than project management. It may involve:
            </p>
            <ul className="mt-4 grid grid-cols-2 gap-y-2 gap-x-4 text-sm text-slate-700">
              {['Lifecycle','Asset information','Governance','Contractual obligations','Compliance','Evidence','Handover','Handback','Disconnected systems and spreadsheets'].map((t) => (
                <li key={t} className="flex items-start gap-2"><ChevronRight className="w-3.5 h-3.5 text-blue-600 mt-0.5 flex-shrink-0" /> {t}</li>
              ))}
            </ul>
            <p className="mt-6 text-lg font-medium text-slate-900">
              When that&rsquo;s the problem, don&rsquo;t start by buying software. Bring us the problem.
            </p>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Keystone works with organisations to understand what&rsquo;s happening, where control or visibility is being lost, and what a better operating model could look like.
            </p>
            <div className="mt-6">
              <Link to="/contact" data-testid="v2-home-consultancy-cta"
                    className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3.5 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors group">
                Talk to Keystone
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 md:p-8">
            <p className="text-[11px] uppercase tracking-[0.22em] text-blue-600 font-semibold mb-6">Our methodology</p>
            <div className="space-y-4">
              {[
                { icon: Search, name: 'Diagnose', body: 'Understand the problem, the operating context and where control is being lost.' },
                { icon: Map, name: 'Map', body: 'Map processes, responsibilities, governance and lifecycle requirements.' },
                { icon: Compass, name: 'Design', body: 'Design the operating model and how technology should fit around it.' },
                { icon: Cog, name: 'Digitise', body: 'Configure Videra around your organisation, not the other way around.' },
                { icon: Users, name: 'Embed', body: 'Roll out with training, adoption and internal ownership.' },
                { icon: TrendingUp, name: 'Improve', body: 'Iterate on the operating model as the organisation evolves.' },
              ].map((s) => {
                const Icon = s.icon;
                return (
                  <div key={s.name} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-white ring-1 ring-slate-200 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-blue-600" strokeWidth={1.8} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{s.name}</p>
                      <p className="text-sm text-slate-600 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ============= WHY VIDERA EXISTS + BEFORE/AFTER ============= */}
    <section className="py-20 md:py-24 bg-slate-50 border-y border-slate-200" data-testid="v2-home-why-videra">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <p className="text-[11px] uppercase tracking-[0.22em] text-blue-600 font-semibold mb-3">Why Videra exists</p>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 leading-tight">
            Built from real operational experience.
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Videra wasn&rsquo;t created in a software development meeting. It came from years spent working inside complex project, lifecycle, healthcare, construction and PFI environments, where we repeatedly saw the same problems. So we started building technology around the way practitioners actually work.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 rounded-2xl overflow-hidden ring-1 ring-slate-200 shadow-xl">
          <div className="relative bg-gradient-to-br from-slate-100 via-slate-50 to-white p-8 md:p-10 border-b md:border-b-0 md:border-r border-slate-200" data-testid="v2-home-before">
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-red-600 mb-4">What we kept seeing</p>
            <h3 className="text-2xl font-medium text-slate-900 tracking-tight mb-6">The current stack.</h3>
            <ul className="space-y-3 text-sm text-slate-700">
              {[
                'Projects managed through spreadsheets',
                'Lifecycle separated from delivery',
                'Evidence scattered across folders and SharePoint',
                'Actions buried in email',
                'Different teams working from different information',
                'Management reports manually reconstructed every month',
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="w-4 h-4 rounded-full bg-red-100 text-red-600 flex-shrink-0 flex items-center justify-center mt-0.5"><span className="text-[10px] font-bold">×</span></span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950 text-white p-8 md:p-10" data-testid="v2-home-after">
            <div className="absolute -right-16 -top-16 w-72 h-72 bg-blue-500/25 blur-3xl rounded-full pointer-events-none" aria-hidden="true" />
            <p className="relative text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-400 mb-4">What we built</p>
            <h3 className="relative text-2xl font-medium tracking-tight mb-6">One connected environment.</h3>
            <ul className="relative space-y-3 text-sm text-slate-200">
              {[
                'Live programme every stakeholder can trust',
                'Evidence attached to the work, always current',
                'Lifecycle and delivery on the same rails',
                'Actions where the work lives, not in inboxes',
                'One connected view for every team',
                'Board packs composed from live data',
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="w-4 h-4 rounded-full bg-emerald-500/25 text-emerald-400 flex-shrink-0 flex items-center justify-center mt-0.5"><CheckCircle2 className="w-3 h-3" /></span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>

    {/* ============= SYNTHESIA VIDEO (preserved) ============= */}
    <section className="py-20 md:py-24 bg-white" data-testid="v2-home-synthesia">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-10">
          <p className="text-[11px] uppercase tracking-[0.22em] text-blue-600 font-semibold mb-3">See the thinking behind Videra</p>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 leading-tight">
            Discover why Videra was created.
          </h2>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">
            How we&rsquo;re approaching the challenge of connecting projects, lifecycle, assurance and evidence.
          </p>
        </div>
        <IntroVideoModal
          src={introVideo.videoUrl}
          poster="/images/healthcare/hc-portfolio-tracker-hero.jpg"
          kicker="90-sec introduction"
          title="See why we built Videra."
          body="In ninety seconds, Keystone explains why we built Videra and walks through how it works across NHS Capital, Healthcare and FM Hard Services, and Tier-1 Construction."
          testid="v2-home-synthesia-video"
        />
      </div>
    </section>

    {/* ============= CORE MESSAGE: PROJECTS · LIFECYCLE · ASSURANCE · EVIDENCE ============= */}
    <section className="py-20 md:py-24 bg-slate-50 border-y border-slate-200" data-testid="v2-home-core-message">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <p className="text-[11px] uppercase tracking-[0.22em] text-blue-600 font-semibold mb-3">The core Videra idea</p>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 leading-tight">
            Projects. Lifecycle. Assurance. Evidence.<br/>Connected around your organisation.
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Videra can connect four fundamental areas of operational delivery.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: ClipboardList, name: 'Projects',  q: 'What are we delivering?' },
            { icon: WorkflowIcon,  name: 'Lifecycle', q: 'What needs to happen next?' },
            { icon: ShieldCheck,   name: 'Assurance', q: 'Are we meeting our requirements?' },
            { icon: FileCheck,     name: 'Evidence',  q: 'Can we prove it?' },
          ].map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.name} className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-blue-500 hover:shadow-lg transition-all" data-testid={`v2-home-core-${c.name.toLowerCase()}`}>
                <div className="w-11 h-11 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5" strokeWidth={1.8} />
                </div>
                <p className="text-lg font-semibold text-slate-900">{c.name}</p>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{c.q}</p>
              </div>
            );
          })}
        </div>
        <p className="mt-10 text-xl md:text-2xl font-medium tracking-tight text-slate-900 text-center">
          One connected view of delivery.
        </p>
      </div>
    </section>

    {/* ============= EXPLORE VIDERA (screenshot marquee, preserved) ============= */}
    <section className="pt-20 md:pt-24 bg-white" data-testid="v2-home-explore-videra">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-6">
          <p className="text-[11px] uppercase tracking-[0.22em] text-blue-600 font-semibold mb-3">Explore Videra</p>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 leading-tight">
            Don&rsquo;t take our word for it. Look inside.
          </h2>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">
            The best way to understand Videra isn&rsquo;t through another software brochure. It&rsquo;s to see how the technology works. Explore the platforms, workflows and interfaces we&rsquo;ve developed around real operational requirements.
          </p>
        </div>
      </div>
      <ScreenshotMarquee shots={marqueeShots} testid="v2-home-marquee" eyebrow={undefined} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <Link to="/tour" data-testid="v2-home-explore-cta"
              className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3.5 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors group">
          <Play className="w-4 h-4" /> Explore Videra
          <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        </Link>
      </div>
    </section>

    {/* ============= BESPOKE SOLUTIONS ============= */}
    <section className="py-20 md:py-24 bg-slate-50 border-y border-slate-200" data-testid="v2-home-bespoke-solutions">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <p className="text-[11px] uppercase tracking-[0.22em] text-blue-600 font-semibold mb-3">Bespoke Solutions</p>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 leading-tight">
            Bespoke doesn&rsquo;t mean starting from zero.
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Our specialist Videra environments provide proven foundations that can be configured around an organisation.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {[
            { name: 'NHS & Healthcare', to: '/videra/healthcare', cta: 'Explore Healthcare',
              image: '/images/nhs/nhs-portfolio-tracker.jpg', icon: Building2,
              body: 'Capital projects, lifecycle programmes, governance, approvals, delivery and evidence designed around healthcare environments.' },
            { name: 'FM & Hard Services', to: '/videra/hard-services', cta: 'Explore Hard Services',
              image: '/images/healthcare/hc-handback-readiness.jpg', icon: Wrench,
              body: 'Projects, assets, lifecycle, operational requirements and evidence connected within one environment.' },
            { name: 'Construction', to: '/videra/construction', cta: 'Explore Construction',
              image: '/images/construction/cp-live-portfolio.jpg', icon: HardHat,
              body: 'From project initiation and governance through delivery, handover and future lifecycle.' },
            { name: 'PFI / P3', to: '/international-p3', cta: 'Explore PFI / P3',
              image: '/images/nhs/nhs-compliance-rails.jpg', icon: ShieldCheck,
              body: 'Contractual obligations, assets, maintenance, lifecycle, assurance, evidence and handback.' },
          ].map((p) => {
            const Icon = p.icon;
            return (
              <Link key={p.name} to={p.to} data-testid={`v2-home-bespoke-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    className="group block bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-blue-500 hover:shadow-xl hover:-translate-y-0.5 transition-all">
                <div className="relative aspect-[16/9] bg-slate-900 overflow-hidden">
                  <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]" />
                  <div className="absolute top-3 right-3 w-10 h-10 rounded-lg bg-white/95 backdrop-blur-sm shadow-md flex items-center justify-center text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                    <Icon className="w-5 h-5" />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-medium text-slate-900 tracking-tight mb-2">{p.name}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{p.body}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-slate-900 group-hover:gap-2 transition-all">
                    {p.cta} <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
        <p className="mt-10 text-lg text-slate-600 italic max-w-3xl">
          These aren&rsquo;t simply software products. They demonstrate what becomes possible when consultancy, operational experience and configurable technology are brought together.
        </p>
      </div>
    </section>

    {/* ============= FOUNDER ============= */}
    <section className="py-20 md:py-24 bg-white" data-testid="v2-home-founder">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-[11px] uppercase tracking-[0.22em] text-blue-600 font-semibold mb-3">The experience behind Videra</p>
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 md:gap-12 items-start">
          <div className="relative flex-shrink-0">
            <img src="/images/team/peter-fulford.jpg" alt="Peter Fulford, Founder"
                 loading="lazy"
                 className="w-32 h-32 md:w-48 md:h-48 rounded-2xl object-cover ring-4 ring-white shadow-xl" />
            <span className="absolute -bottom-3 -right-3 bg-slate-900 text-white text-[9px] uppercase tracking-[0.22em] font-bold px-2.5 py-1 rounded-full shadow-lg">Founder</span>
          </div>
          <div>
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 leading-tight">Peter Fulford</h2>
            <p className="mt-1 text-slate-500">Founder &amp; Director, Videra Technology</p>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              After more than two decades working across complex project delivery, lifecycle management, healthcare estates, construction and PFI environments, Peter founded Keystone Strategic Consultants and Videra Technology.
            </p>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              The idea behind Videra came directly from that experience. Rather than asking organisations to change the way they operate to fit another piece of software, the objective was different:
            </p>
            <p className="mt-5 text-xl md:text-2xl font-medium tracking-tight text-slate-900 leading-snug">
              &ldquo;Understand how the organisation works, then make the technology work around it.&rdquo;
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              That principle continues to shape everything we build.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button type="button" onClick={() => setAskPeterOpen(true)} data-testid="v2-home-founder-meet"
                      className="inline-flex items-center gap-2 bg-slate-900 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors">
                <MessageCircle className="w-4 h-4" /> Meet Peter
              </button>
              <Link to="/about" data-testid="v2-home-founder-about"
                    className="inline-flex items-center gap-1.5 text-slate-700 hover:text-slate-900 text-sm font-medium underline-offset-4 hover:underline">
                About Keystone
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ============= WHERE DO YOU WANT TO START? ============= */}
    <section className="py-20 md:py-24 bg-slate-50 border-y border-slate-200" data-testid="v2-home-decision">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <p className="text-[11px] uppercase tracking-[0.22em] text-blue-600 font-semibold mb-3">Your starting point</p>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 leading-tight">
            Where do you want to start?
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Column 1 */}
          <div className="bg-white border border-slate-200 rounded-2xl p-8 flex flex-col hover:shadow-xl hover:-translate-y-0.5 transition-all" data-testid="v2-home-decision-pm">
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-blue-600 mb-3">Option 1</span>
            <p className="text-lg font-semibold text-slate-900 leading-snug">
              I need better project control now.
            </p>
            <p className="mt-3 text-slate-600 leading-relaxed text-sm">
              <strong className="text-slate-900">Start with Videra PM.</strong> Choose an industry template, personalise your platform and subscribe monthly. No consultancy programme required.
            </p>
            <a href="https://pm.videra.app" data-testid="v2-home-decision-pm-cta"
               className="mt-auto pt-6 inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:gap-3 transition-all">
              Start Videra PM <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          {/* Column 2 */}
          <div className="bg-white border border-slate-200 rounded-2xl p-8 flex flex-col hover:shadow-xl hover:-translate-y-0.5 transition-all" data-testid="v2-home-decision-talk">
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-blue-600 mb-3">Option 2</span>
            <p className="text-lg font-semibold text-slate-900 leading-snug">
              We have a more complex problem.
            </p>
            <p className="mt-3 text-slate-600 leading-relaxed text-sm">
              <strong className="text-slate-900">Talk to Keystone.</strong> We&rsquo;ll understand the problem first and determine what combination of consultancy and technology makes sense. No predetermined solution.
            </p>
            <Link to="/contact" data-testid="v2-home-decision-talk-cta"
                  className="mt-auto pt-6 inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:gap-3 transition-all">
              Talk to Keystone <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Column 3 */}
          <div className="bg-slate-950 text-white border border-slate-800 rounded-2xl p-8 flex flex-col hover:shadow-xl hover:-translate-y-0.5 transition-all" data-testid="v2-home-decision-bespoke">
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-400 mb-3">Option 3</span>
            <p className="text-lg font-semibold leading-snug">
              We want Videra built around us.
            </p>
            <p className="mt-3 text-slate-300 leading-relaxed text-sm">
              <strong className="text-white">Let&rsquo;s design it together.</strong> We&rsquo;ll map your requirements and configure a Videra environment around your organisation, processes and operating model.
            </p>
            <Link to="/contact" data-testid="v2-home-decision-bespoke-cta"
                  className="mt-auto pt-6 inline-flex items-center gap-2 text-sm font-medium text-blue-300 hover:text-blue-200 hover:gap-3 transition-all">
              Build Our Videra <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>

    {/* ============= FINAL MESSAGE ============= */}
    <section className="py-20 md:py-24 bg-white" data-testid="v2-home-final">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-4xl md:text-5xl font-medium tracking-tight text-slate-900 leading-[1.05]">
          Start simple. Grow sophisticated.
        </h2>
        <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
          You don&rsquo;t need to transform everything at once. Start with the problem you need to solve today.
        </p>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto text-slate-700">
          <p><strong className="text-slate-900">Videra PM</strong><br/><span className="text-sm text-slate-500">when you need something now.</span></p>
          <p><strong className="text-slate-900">Keystone</strong><br/><span className="text-sm text-slate-500">when you need expertise.</span></p>
          <p><strong className="text-slate-900">Bespoke Videra</strong><br/><span className="text-sm text-slate-500">when the technology needs to fit your world.</span></p>
        </div>
        <p className="mt-10 text-xl md:text-2xl font-medium tracking-tight text-slate-900">
          Bring us the problem. We&rsquo;ll help you decide where to start.
        </p>
        <div className="mt-8">
          <Link to="/contact" data-testid="v2-home-final-cta"
                className="inline-flex items-center gap-2 bg-slate-900 text-white px-7 py-4 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors group">
            Talk to Keystone
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  </div>
  );
};

export default V2Home;
